import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
// import { useAuth } from "../Hooks/AuthProvider";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../Utils/firebaseConfig";
// Brent branding assets/colors (customer login should always be Brent)
const BRAND_LOGO = "/images/icons/brent-logo.png";
const BRAND_ORANGE = "#f05a28";
const BRAND_TEXT = "#242424";
const BRAND_BG = "#ffffff"; // keep customer page background white

const CustomerLogin = () => {
  const navigate = useNavigate();
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Initialize Firebase Functions
  React.useEffect(() => {
    const functions = getFunctions();
    if (process.env.NODE_ENV === "development") {
      connectFunctionsEmulator(functions, "localhost", 5001);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      let emailToUse = emailOrUsername;

      // Check if input is a username (doesn't contain @) and lookup email
      if (!emailOrUsername.includes("@")) {
        const functions = getFunctions();
        const lookupEmail = httpsCallable(functions, "lookupEmailByUsername");
        try {
          const lookupResponse = await lookupEmail({ username: emailOrUsername });
          if (lookupResponse.data.success) {
            emailToUse = lookupResponse.data.email;
          } else {
            setError("Username not found");
            setLoading(false);
            return;
          }
        } catch (lookupError) {
          // If lookup fails, try treating it as email anyway
          if (lookupError.code === "functions/not-found") {
            setError("Username not found");
            setLoading(false);
            return;
          }
          // For other errors, fall through to try as email
          emailToUse = emailOrUsername;
        }
      }

      // Sign in with email/password
      await signInWithEmailAndPassword(auth, emailToUse, password);
      
      // Get user role to verify they're a customer
      const functions = getFunctions();
      const getUserRole = httpsCallable(functions, "getUserRole");
      const roleResponse = await getUserRole();

      if (roleResponse.data.role === "customer") {
        // Customer user - redirect to customer project equipment page
        navigate("/customer/project-equipment");
      } else {
        // Not a customer - sign them out and show error
        await auth.signOut();
        setError("This login is for customers only. Please use the main app login.");
      }
    } catch (err) {
      console.error("[CustomerLogin] Error:", err);
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: BRAND_BG,
        padding: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: "100%",
          padding: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        <img
          src={BRAND_LOGO}
          alt="Logo"
          style={{ maxWidth: "200px", width: "auto", height: "auto" }}
        />
        <Typography
          variant="h4"
          sx={{
            color: BRAND_ORANGE,
            fontWeight: 700,
            letterSpacing: 0.1,
            textTransform: "uppercase",
            fontSize: 20,
            lineHeight: 1.2,
          }}
        >
          Customer Portal
        </Typography>
        <Typography
          variant="body2"
          color={BRAND_TEXT}
          textAlign="center"
        >
          Sign in to view your project equipment
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: "100%" }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleLogin} sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Email or Username"
            type="text"
            value={emailOrUsername}
            onChange={(e) => setEmailOrUsername(e.target.value)}
            required
            fullWidth
            disabled={loading}
            helperText="Enter your email address or username"
            sx={{
              "& label.Mui-focused": { color: BRAND_ORANGE },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#d9d9d9" },
                "&:hover fieldset": { borderColor: BRAND_ORANGE },
                "&.Mui-focused fieldset": { borderColor: BRAND_ORANGE },
              },
            }}
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            disabled={loading}
            sx={{
              "& label.Mui-focused": { color: BRAND_ORANGE },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#d9d9d9" },
                "&:hover fieldset": { borderColor: BRAND_ORANGE },
                "&.Mui-focused fieldset": { borderColor: BRAND_ORANGE },
              },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              backgroundColor: BRAND_ORANGE,
              "&:hover": {
                backgroundColor: "#d84f22",
              },
              height: "48px",
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Sign In"
            )}
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default CustomerLogin;

